import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../button';
import { IGetCarQuery } from '../../services/carService/__generated__/queries.generated';
import { URLS } from '../../../constants/urls';
import { Colors } from '../../styles/colors';
import { carouselCardWidthCss } from './carCarouselCardWidth';
import { carCardPrice, CarCardPriceSlot } from '../../utils/carCardPrice';

type ICarProps = IGetCarQuery['car'] & {
  renderButtons?: () => {
    text: string;
    onClick: () => void;
  }[];
  asDealer?: boolean;
  showButtons?: boolean;
  onClick?: () => void;
  /** Spec 022: daily, the week price (weekly) or the monthly price (monthly sections). */
  priceMode?: CarCardPriceSlot;
};

const CardContainer = styled.div`
  ${tw`
    flex
    flex-col
    overflow-hidden
    cursor-pointer
    flex-shrink-0
  `}
  background: ${Colors.White};
  border: 1px solid ${Colors.BorderLight};
  border-radius: 12px;
  ${carouselCardWidthCss}
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition:
    opacity 150ms ease-out,
    transform 150ms ease-out,
    box-shadow 150ms ease-out;
  will-change: transform, opacity;

  &:hover {
    opacity: 0.88;
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: opacity 150ms ease-out;
    &:hover {
      transform: none;
      opacity: 0.88;
    }
  }
`;

const ImageContainer = styled.div`
  ${tw`
    relative
    w-full
    overflow-hidden
  `};
  /* 16:10 aspect ratio */
  aspect-ratio: 16 / 10;
  background-color: ${Colors.BgSecondary};
  border-radius: 12px 12px 0 0;
`;

const CarImage = styled.img`
  ${tw`
    w-full
    h-full
    object-cover
  `};
`;

const ContentContainer = styled.div`
  ${tw`
    flex
    flex-col
    px-3
    pt-2
    pb-3
  `};
  gap: 2px;
`;

const CarName = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: ${Colors.TextPrimary};
  ${tw`truncate`};
`;

const MetaRow = styled.div`
  ${tw`
    flex
    items-center
    flex-wrap
 w-full max-w-full min-w-0 box-border`};
  margin: 0;
  gap: 0;
  font-size: 14px;
  line-height: 1.25;
  color: ${Colors.TextSecondary};
`;

const MetaText = styled.span`
  font-weight: 400;
  color: ${Colors.TextSecondary};
`;

const MetaDot = styled.span`
  margin: 0 5px;
  font-weight: 400;
  color: ${Colors.TextSecondary};
`;

const StarIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Champagne};
  font-size: 11px;
  margin-right: 3px;
`;

const RatingValue = styled.span`
  font-weight: 600;
  color: ${Colors.TextPrimary};
`;

const TripText = styled.span`
  margin-left: 4px;
  font-weight: 400;
  color: ${Colors.TextSecondary};
`;

const PriceContainer = styled.div`
  ${tw`
    flex
    items-baseline
  `};
  margin-top: 4px;
  gap: 1px;
`;

const PriceText = styled.span`
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
  color: ${Colors.TextPrimary};
`;

const PriceUnit = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.25;
  color: ${Colors.TextSecondary};
`;

const ButtonsContainer = styled.div`
  ${tw`
    flex
    flex-col
    mt-4
  `};
  gap: 8px;
`;

export function Car(props: ICarProps) {
  const {
    thumbnailUrl,
    weekdayPrice,
    year,
    make,
    model,
    renderButtons,
    asDealer,
    originalPrice,
    listedSalePrice,
    bookings,
    showButtons = false,
    onClick,
    priceMode = 'daily',
  } = props;

  // Trip count logic
  const tripCount = bookings?.length || 0;
  const rating = 4.9; // Mock rating as in mobile image example
  const hasRatings = tripCount > 0;

  const cardPrice = carCardPrice({
    slot: priceMode,
    displayPrices: props.tripSettings?.displayPrices,
    dailyPrice: weekdayPrice,
    monthlyPrice: props.monthlyPrice,
  });
  const displayPrice =
    asDealer && priceMode !== 'monthly' ? listedSalePrice || originalPrice : cardPrice.amount;
  const priceUnit = asDealer && priceMode !== 'monthly' ? '/day' : cardPrice.suffix;
  const carDisplayName = `${make} ${model}`;

  return (
    <CardContainer onClick={onClick}>
      <ImageContainer>
        <CarImage
          src={
            thumbnailUrl
              ? `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${thumbnailUrl}`
              : `${URLS.S3_BUCKET.CAR_THUMBNAILS}/car-images/default-car.jpg`
          }
          alt={carDisplayName}
        />
      </ImageContainer>

      <ContentContainer>
        <CarName title={carDisplayName}>{carDisplayName}</CarName>

        <MetaRow>
          <MetaText>{year}</MetaText>
          <MetaDot aria-hidden>·</MetaDot>
          {hasRatings ? (
            <>
              <StarIcon icon={faStar} aria-hidden />
              <RatingValue>{rating.toFixed(2)}</RatingValue>
              <TripText>({tripCount} trips)</TripText>
            </>
          ) : (
            <>
              <StarIcon icon={faStar} aria-hidden />
              <MetaText>{!rating ? '-' : rating.toFixed(2)}</MetaText>
            </>
          )}
        </MetaRow>

        <PriceContainer>
          <PriceText>${displayPrice}</PriceText>
          <PriceUnit>{priceUnit}</PriceUnit>
        </PriceContainer>

        {showButtons && renderButtons && (
          <ButtonsContainer>
            {renderButtons().map((button, index) => (
              <Button
                key={index}
                text={button.text}
                onClick={button.onClick}
                theme={index === 0 ? 'filled' : 'outlined'}
              />
            ))}
          </ButtonsContainer>
        )}
      </ContentContainer>
    </CardContainer>
  );
}
